/**
 * The open/close animation core shared by every hand-openable thing in the
 * viewer: room doors (`doorOpenState`) and cabinet fronts
 * (`cabinetFrontOpenState`), both in `runtimeMeshState.js`.
 *
 * PURE. Each function takes an entry (or undefined for "never touched") and
 * returns progress or a NEW entry; the caller owns the Map it lives in, and
 * any extra fields it keeps on the entry (a door's `triggerRoomId`,
 * `manualClose`) are carried through untouched.
 *
 *   closed ──beginOpening──▶ opening ──(lands)──▶ open
 *     ▲                        │                   │
 *     └──(lands)── closing ◀───┴── beginClosing ───┘
 *
 * An in-flight entry carries `animStartedAt` and `fromT` — the progress it
 * started FROM — so a reversal mid-swing animates out of the current pose
 * rather than snapping to an endpoint first, and the duration scales with the
 * distance left to travel (constant speed, not constant wall-clock time).
 */

export const OPEN_CLOSE_STATE = {
  closed: 'closed',
  opening: 'opening',
  open: 'open',
  closing: 'closing',
};

/** The resting / in-flight state of an entry; an absent entry is closed. */
export const openCloseStateOf = (entry) =>
  entry?.state || OPEN_CLOSE_STATE.closed;

/**
 * Progress toward open at `now` (0 shut, 1 fully open), plus the entry to
 * store: the same entry while animating, promoted to its terminal state once
 * the animation has landed.
 *
 * @returns {{ t: number, entry: object|undefined }}
 */
export function advanceOpenClose(entry, now, durationMs) {
  const s = openCloseStateOf(entry);
  if (s === OPEN_CLOSE_STATE.closed) return { t: 0, entry };
  if (s === OPEN_CLOSE_STATE.open) return { t: 1, entry };

  const target = s === OPEN_CLOSE_STATE.opening ? 1 : 0;
  const fromT = entry.fromT ?? (target === 1 ? 0 : 1);
  const settled = {
    ...entry,
    state: target === 1 ? OPEN_CLOSE_STATE.open : OPEN_CLOSE_STATE.closed,
  };
  const span = Math.abs(target - fromT);
  if (span < 1e-6) return { t: target, entry: settled };

  // Floored at 0: an entry may be scheduled to start LATER (a staged open —
  // a roll-out tray waits for its doors), and until then it holds its pose
  // rather than running backwards.
  const progress = Math.max(
    0,
    Math.min(1, (now - entry.animStartedAt) / (durationMs * span))
  );
  const t = fromT + (target - fromT) * progress;
  return { t, entry: progress >= 1 ? settled : entry };
}

/**
 * Start (or keep) opening. Returns null when the entry is already opening or
 * open — the caller leaves it alone. `startAt` (default `now`) may lie in the
 * future: the entry is `opening` from now, holding the pose it has at `now`
 * until then.
 */
export function beginOpening(entry, now, durationMs, startAt = now) {
  const s = openCloseStateOf(entry);
  if (s === OPEN_CLOSE_STATE.opening || s === OPEN_CLOSE_STATE.open) {
    return null;
  }
  return {
    ...entry,
    state: OPEN_CLOSE_STATE.opening,
    animStartedAt: startAt,
    fromT: advanceOpenClose(entry, now, durationMs).t,
  };
}

/**
 * Start (or keep) closing. Returns null when the entry is already closing or
 * closed. `startAt` as for `beginOpening`.
 */
export function beginClosing(entry, now, durationMs, startAt = now) {
  const s = openCloseStateOf(entry);
  if (s === OPEN_CLOSE_STATE.closed || s === OPEN_CLOSE_STATE.closing) {
    return null;
  }
  return {
    ...entry,
    state: OPEN_CLOSE_STATE.closing,
    animStartedAt: startAt,
    fromT: advanceOpenClose(entry, now, durationMs).t,
  };
}

/**
 * When an entry comes to rest: `-Infinity` for one at rest already, else the
 * moment its animation lands (it travels at the core's constant speed, so the
 * time is the distance left times `durationMs`).
 */
export function landsAt(entry, durationMs) {
  const s = openCloseStateOf(entry);
  if (s === OPEN_CLOSE_STATE.open || s === OPEN_CLOSE_STATE.closed) {
    return -Infinity;
  }
  const target = s === OPEN_CLOSE_STATE.opening ? 1 : 0;
  const fromT = entry.fromT ?? (target === 1 ? 0 : 1);
  return entry.animStartedAt + durationMs * Math.abs(target - fromT);
}

/** True when a toggle should open (it is shut or on its way shut). */
export const toggleWouldOpen = (entry) => {
  const s = openCloseStateOf(entry);
  return s === OPEN_CLOSE_STATE.closed || s === OPEN_CLOSE_STATE.closing;
};
